import moment from 'moment'
import React, { useMemo } from 'react'

//UI
import { Button, Flex, Spacer, Text } from '@chakra-ui/react'

import DownloadButton from '@/components/Elements/Button/DownloadButton'
import ViewFileButton from '@/components/Elements/Button/ViewFileButton'

import { TooltipCell, NormalTextCellRenderer } from '@/components/Table/components/Cell/ToolTipCell'
//Hooks

//Helper
import { camelToWords, numberWithCommas } from '@/utils/stringHelper'
import { createColumnHelper } from '@tanstack/react-table'
import EditableTable from '@/components/Table/EditableTable'
import { DateCell } from '@/components/Table/components/Cell'
import { useTranslation } from 'react-i18next'
import { createExcelBlob } from '@/utils/fileHelper'
import { saveAs } from 'file-saver'
import { useDispatch } from 'react-redux'
import { showModal } from '@/stores/ui/modal/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { useWatch } from 'react-hook-form'

const VendorHistoryTable = ({ data }) => {
  const costBudgetType = useWatch({ name: 'costBudgetType' })
  const RFQCode = useWatch({ name: 'RFQCode' })
  const columnHelper = createColumnHelper()
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const handleDownloadQuotationDetailExcel = (history) => {
    try {
      let excelData = []
      const quotationDetails = data?.quotationDetails ?? []
      if (!quotationDetails || quotationDetails.length === 0) {
        excelData = [
          {
            Item: 1,
            Description: '',
            'Model Number': '',
            Quantity: '',
            unit: '',
            'Unit Price': '',
            Discount: '',
            'Total Price': '',
            Category: '',
            Country: '',
            AL: '',
            ECCN: '',
            'HS Code': '',
          },
        ]
      } else {
        excelData = quotationDetails.map((row, i) => {
          return {
            Item: i + 1,
            Description: row.description,
            'Model Number': row.modelNumber,
            Quantity: row.quantity,
            unit: row.unit,
            'Unit Price': row.unitRate,
            Discount: row.discount,
            'Total Price': row.totalPrice,
            Category: row.category,
            Country: row.country,
            AL: row.AL,
            ECCN: row.ECCN,
            'HS Code': row.HSCode,
          }
        })
      }

      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, `E-Approval Quotation Detail-${Date.now()}.xlsx`)
    } catch (err) {
      console.log(err)
    }
  }

  const columnDef = useMemo(
    () => [
      columnHelper.display({
        id: 'vendorHistoryIndex',
        size: 30,
        header: () => <span></span>,
        cell: ({ row: { index } }) => <TooltipCell text={index + 1} />,
      }),
      columnHelper.accessor('tenderDocumentIndex', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_HISTORY_TENDER_INDEX')}</span>,
        cell: ({ getValue }) => {
          const displayValue =
            getValue() === 0 ? t('VENDOR_UPLOAD_PAGE_TENDER') : t('VENDOR_UPLOAD_PAGE_ADDENDUM') + getValue()
          return <TooltipCell text={displayValue} />
        },
      }),
      columnHelper.accessor('actionDate', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_HISTORY_DATE')}</span>,
        cell: (cell) => <DateCell format="DD-MMM-YYYY HH:mm:ss" {...cell} />,
      }),
      columnHelper.accessor('userName', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_HISTORY_USER')}</span>,
      }),
      columnHelper.accessor('historyType', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_HISTORY_ACTION')}</span>,
        cell: NormalTextCellRenderer,
      }),
      columnHelper.accessor('remark', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_HISTORY_REMARK')}</span>,

        size: 200,
        cell: ({ getValue, row: { original } }) => {
          let displayValue = getValue()
          if (original?.historyType === 'upload') {
            displayValue = 'Uploaded a new quotation, with remark: ' + getValue()
          }
          return <TooltipCell text={displayValue} />
        },
      }),
      columnHelper.accessor('budget', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_HISTORY_BUDGET')}</span>,

        size: 120,
        cell: ({ getValue, row: { original } }) => {
          if (original.historyType !== 'upload') {
            return <>-</>
          }

          return <TooltipCell text={`$${numberWithCommas(getValue())}`} />
        },
      }),
      columnHelper.accessor('validDate', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_HISTORY_VALID_DATE')}</span>,

        cell: ({ getValue, row: { original } }) => {
          if (original.historyType !== 'upload') {
            return <>-</>
          }
          return <TooltipCell text={getValue() ? moment(getValue()).format('DD-MMM-YYYY') : '-'} />
        },
      }),
      columnHelper.accessor('files', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_FILES')}</span>,

        cell: ({ getValue, row: { original } }) => {
          const files = getValue()
          return (
            <Flex direction={'column'} gap="2">
              {files.map((file) => {
                return (
                  <Flex gap={2} alignItems={'center'} key={file?._id}>
                    <ViewFileButton fileId={file?._id} />
                    <DownloadButton fileId={file?._id} fileName={file?.originalFileName} />
                  </Flex>
                )
              })}
            </Flex>
          )
        },
      }),
      columnHelper.accessor('technicalSupportDocuments', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_TECHNICAL_SUPPORT_DOCUMENTS')}</span>,

        cell: ({ getValue, row: { original } }) => {
          const files = getValue() ?? []

          return (
            <Flex direction={'column'} gap="2">
              {files.map((file) => {
                return (
                  <Flex gap={2} alignItems={'center'} key={file?._id}>
                    <ViewFileButton fileId={file?._id} />
                    <DownloadButton fileId={file?._id} fileName={file?.originalFileName} />
                  </Flex>
                )
              })}
            </Flex>
          )
        },
      }),
      columnHelper.display({
        id: 'viewDetail',
        size: 80,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ row: { original } }) => {
          if (original.historyType !== 'upload') {
            return <></>
          }

          return (
            <Button
              colorScheme="blue"
              onClick={(e) =>
                dispatch(
                  showModal({
                    modalType: MODAL_TYPES.VIEW_QUOTATION_DETAIL,
                    modalProps: {
                      history: original,
                      costBudgetType: costBudgetType,
                      RFQCode: RFQCode,
                    },
                  })
                )
              }
            >
              {t('VENDOR_UPLOAD_PAGE_HISTORY_DETAIL')}
            </Button>
          )
        },
      }),
    ],
    [columnHelper, RFQCode]
  )
  return <EditableTable textAlign={'left'} columnDef={columnDef} data={data} disabled={true} tableWidth="100%" />
}

export default VendorHistoryTable
